/**
 * Sidebar module-creation hook: the new-module wizard state cluster, the
 * synchronous double-submit guard ref, and the async `handleCreateModule`.
 *
 * `handleCreateModule`'s dependency array keeps `navigate` even though it is
 * unused, to preserve the original memo identity. Position/Module-shape math is
 * delegated to the `buildNewModule` kernel (pinned by moduleCreation.test.ts).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { useNavigate } from 'react-router-dom';
import { createPluginInstance } from '@/modules/_infrastructure/pluginApi';
import { prefetchModuleChunk } from '@/lib/prefetch-module';
import { queryKeys } from '@/lib/query/keys';
import type { Conversation } from '@/types/conversations';
import type { Module } from '@/types/modules';
import { PLUGIN_TYPE_MAP } from '../constants';
import { buildNewModule } from '../moduleCreation';

interface UseModuleCreationArgs {
  activeConversation: Conversation | null;
  addModule: (conversationId: string, module: Module) => Promise<void> | void;
  navigate: ReturnType<typeof useNavigate>;
  workspaceId: string;
  queryClient: QueryClient;
}

export function useModuleCreation({
  activeConversation,
  addModule,
  navigate,
  workspaceId,
  queryClient,
}: UseModuleCreationArgs) {
  const [isNewModuleMenuOpen, setIsNewModuleMenuOpen] = useState(false);
  const [newModuleStep, setNewModuleStep] = useState<{ type: string; label: string; emoji: string; nativeOnly?: boolean } | null>(null);
  const [newModuleName, setNewModuleName] = useState('');
  const [newModuleNameError, setNewModuleNameError] = useState('');
  const [newModuleSearch, setNewModuleSearch] = useState('');
  const [isCreatingModule, setIsCreatingModule] = useState(false);
  const isCreatingModuleRef = useRef(false);
  const newModuleNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (newModuleStep && newModuleNameInputRef.current) {
      newModuleNameInputRef.current.focus();
      newModuleNameInputRef.current.select();
    }
  }, [newModuleStep]);

  const handleCreateModule = useCallback(async (type: string, label: string, nativeOnly?: boolean) => {
    if (!activeConversation?.id || isCreatingModuleRef.current) return;
    isCreatingModuleRef.current = true;
    setIsCreatingModule(true);
    // Warm the module's lazy chunk while the create round-trips — the new
    // module renders inline in chat immediately after, with no hover
    // opportunity (no-op for bundled types, deduped per chunk).
    prefetchModuleChunk(type);
    try {
      const newModule = buildNewModule({
        conversationId: activeConversation.id,
        type,
        label,
        existingModules: activeConversation.modules || [],
        nativeOnly,
      });

      await addModule(activeConversation.id, newModule);

      const pluginId = PLUGIN_TYPE_MAP[type];
      if (pluginId) {
        try {
          await createPluginInstance(pluginId, activeConversation.id, {});
        } catch (err) {
          console.warn('[Sidebar] Plugin instance creation failed (non-blocking):', err);
        }
      }

      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });

      window.dispatchEvent(new CustomEvent('xavior:log-chat-message', {
        detail: { message: `Show ${label}`, moduleId: newModule.id },
      }));

      setIsNewModuleMenuOpen(false);
      setNewModuleStep(null);
      setNewModuleName('');
      setNewModuleNameError('');
    } catch (error) {
      console.error('[Sidebar] Failed to create module:', error);
      setNewModuleNameError('Failed to create module. Please try again.');
    } finally {
      isCreatingModuleRef.current = false;
      setIsCreatingModule(false);
    }
  }, [activeConversation, addModule, navigate, workspaceId, queryClient]);

  return {
    isNewModuleMenuOpen,
    setIsNewModuleMenuOpen,
    newModuleStep,
    setNewModuleStep,
    newModuleName,
    setNewModuleName,
    newModuleNameError,
    setNewModuleNameError,
    newModuleSearch,
    setNewModuleSearch,
    isCreatingModule,
    newModuleNameInputRef,
    handleCreateModule,
  };
}
